import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import { Card, Button, Badge } from '../../components/UI'
import { Terminal, Send, Wrench, RefreshCw, MessageSquare, ExternalLink, Activity } from 'lucide-react'

const COMMANDS = [
    { id: 'maintenance_on', label: 'Mantenimiento ON', icon: '🔧', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', hasMessage: true },
    { id: 'maintenance_off', label: 'Mantenimiento OFF', icon: '✅', color: 'bg-green-500/10 text-green-400 border-green-500/20' },
    { id: 'force_refresh', label: 'Forzar Recarga', icon: '🔄', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    { id: 'show_message', label: 'Mostrar Mensaje', icon: '💬', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20', hasMessage: true, hasType: true },
    { id: 'redirect', label: 'Redirigir', icon: '↗️', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', hasUrl: true },
    { id: 'collect_diagnostics', label: 'Diagnósticos', icon: '🔬', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
    { id: 'clear_cache', label: 'Limpiar Caché (Revocación)', icon: '🗑️', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
]

export default function CommandsPage() {
    const [licenses, setLicenses] = useState([])
    const [history, setHistory] = useState([])
    const [selectedLicense, setSelectedLicense] = useState('')
    const [expandHistory, setExpandHistory] = useState(false) // Estado para historial colapsado
    const [selectedCommand, setSelectedCommand] = useState(null)
    const [messageText, setMessageText] = useState('')
    const [messageType, setMessageType] = useState('info')
    const [messageMode, setMessageMode] = useState('toast')
    const [redirectUrl, setRedirectUrl] = useState('')
    const [loading, setLoading] = useState(false)

    useEffect(() => { fetchData() }, [])

    const fetchData = async () => {
        const { data: lics } = await supabase.from('licenses').select('id, client_name, status')
        setLicenses(lics || [])
        if (lics?.length && !selectedLicense) setSelectedLicense(lics[0].id)
        const { data: cmds } = await supabase.from('remote_commands').select('*, licenses(client_name)').order('created_at', { ascending: false }).limit(50)
        setHistory(cmds || [])
    }

    const sendCommand = async (cmdId) => {
        if (!selectedLicense) { toast.error('Selecciona una licencia'); return }
        let payload = {}
        if (cmdId === 'maintenance_on') payload = { message: messageText || 'Sistema en mantenimiento' }
        if (cmdId === 'show_message') payload = { text: messageText || 'Mensaje', type: messageType, mode: messageMode, duration: 8000 }
        if (cmdId === 'redirect') payload = { url: redirectUrl || '/' }
        setLoading(true)
        try {
            const { error } = await supabase.rpc('tower_send_command', { p_license_id: selectedLicense, p_command: cmdId, p_payload: payload })
            if (error) throw error
            toast.success(`Comando "${cmdId}" enviado`)
            setSelectedCommand(null); setMessageText(''); setRedirectUrl('')
            fetchData()
        } catch (e) { toast.error(e.message) }
        setLoading(false)
    }

    return (
        <div className="p-6 max-w-7xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3"><Terminal className="text-neon-blue" /> Centro de Comandos</h1>
                <p className="text-gray-500 text-sm">Envía comandos remotos a los proyectos desplegados</p>
            </div>

            <Card className="mb-8">
                <label className="text-sm text-gray-400 block mb-2">Proyecto Destino</label>
                <select className="w-full bg-charcoal border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-neon-blue" value={selectedLicense} onChange={e => setSelectedLicense(e.target.value)} aria-label="Proyecto destino">
                    <option value="" disabled>Seleccionar destino...</option>
                    <option value="ALL" className="font-bold text-neon-blue">📢 TODOS LOS PROYECTOS (Broadcast)</option>
                    {licenses.map(l => <option key={l.id} value={l.id}>{l.client_name} ({l.status})</option>)}
                </select>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {COMMANDS.map(cmd => (
                    <button key={cmd.id} onClick={() => cmd.hasMessage || cmd.hasUrl ? setSelectedCommand(cmd) : sendCommand(cmd.id)} disabled={loading} className={`glass-panel rounded-xl p-5 text-left hover:scale-[1.02] transition-transform border ${cmd.color}`} aria-label={cmd.label}>
                        <span className="text-3xl block mb-3">{cmd.icon}</span>
                        <p className="text-white font-medium text-sm">{cmd.label}</p>
                    </button>
                ))}
            </div>

            {selectedCommand && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="w-full max-w-md glass-panel rounded-xl p-6 relative">
                        <button onClick={() => setSelectedCommand(null)} className="absolute top-4 right-4 text-gray-500 hover:text-white" aria-label="Cerrar">✕</button>
                        <h2 className="text-xl font-bold text-white mb-4">{selectedCommand.icon} {selectedCommand.label}</h2>
                        <div className="space-y-4">
                            {selectedCommand.hasMessage && <textarea rows="3" className="w-full bg-charcoal border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-neon-blue" value={messageText} onChange={e => setMessageText(e.target.value)} placeholder="Texto..." aria-label="Mensaje" />}
                            {selectedCommand.hasType && (
                                <div className="grid grid-cols-2 gap-3">
                                    <select className="w-full bg-charcoal border border-white/10 rounded-lg px-4 py-2 text-white" value={messageType} onChange={e => setMessageType(e.target.value)} aria-label="Tipo">
                                        <option value="info">Info</option>
                                        <option value="warning">Warning</option>
                                        <option value="error">Error</option>
                                        <option value="success">Success</option>
                                    </select>
                                    <select className="w-full bg-charcoal border border-white/10 rounded-lg px-4 py-2 text-white" value={messageMode} onChange={e => setMessageMode(e.target.value)} aria-label="Modo">
                                        <option value="toast">Flotante</option>
                                        <option value="modal">Modal</option>
                                        <option value="fullscreen">Pantalla Completa</option>
                                    </select>
                                </div>
                            )}
                            {selectedCommand.hasUrl && <input type="text" className="w-full bg-charcoal border border-white/10 rounded-lg px-4 py-3 text-white font-mono text-sm" value={redirectUrl} onChange={e => setRedirectUrl(e.target.value)} placeholder="/login" aria-label="URL" />}
                            <Button className="w-full justify-center" onClick={() => sendCommand(selectedCommand.id)} disabled={loading}><Send size={16} /> Enviar</Button>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex items-center justify-between mb-4 mt-10">
                <h2 className="text-xl font-bold text-white">Historial</h2>
                <button
                    onClick={() => setExpandHistory(!expandHistory)}
                    className="text-xs text-neon-blue hover:text-white transition-colors"
                >
                    {expandHistory ? 'Ver menos' : 'Ver todo'}
                </button>
            </div>

            <div className="space-y-2">
                {history.slice(0, expandHistory ? undefined : 5).map((cmd) => (
                    <div key={cmd.id} className="p-3 bg-white/5 rounded border border-white/5 flex justify-between items-center group hover:bg-white/10 transition-colors">
                        <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${cmd.status === 'pending' ? 'bg-yellow-500 animate-pulse' : cmd.status === 'executed' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <div>
                                <div className="text-sm font-mono text-white">{cmd.command}</div>
                                <div className="text-xs text-gray-500">{new Date(cmd.created_at).toLocaleString()}</div>
                            </div>
                        </div>
                        <div className="text-xs text-gray-400 font-mono hidden md:block group-hover:text-white">
                            {JSON.stringify(cmd.payload).substring(0, 30)}...
                        </div>
                    </div>
                ))}
                {history.length === 0 && <div className="text-center text-gray-600 py-4">Sin actividad reciente</div>}

                {!expandHistory && history.length > 5 && (
                    <button
                        onClick={() => setExpandHistory(true)}
                        className="w-full py-2 text-xs text-gray-500 hover:text-white bg-white/5 hover:bg-white/10 rounded border border-dashed border-white/10 mt-2 transition-colors"
                    >
                        +{history.length - 5} comandos más...
                    </button>
                )}
            </div>
        </div>
    )
}
